import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSeo } from '@/hooks/useSeo';
import { SITE_NAME } from '@/lib/seo';

/*
  The pages a marketplace is required to have, and which Google Play will not list an app without:
  a privacy policy, terms of use, and a publicly reachable explanation of how to delete an account.

  Written as plain components rather than fetched documents. They change perhaps twice a year, they
  must render with no network and no account, and a CMS for four pages is four pages of content and
  a database table nobody remembers to back up.

  The deletion page in particular has to be reachable *without installing the app and without
  signing in* — that is the whole point of the requirement, and it is why it sits on a public route
  rather than inside the account area.
*/

function Prose({ title, updated, children }: { title: string; updated: string; children: ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="font-display text-3xl font-bold text-body">{title}</h1>
      <p className="mt-2 text-xs text-faint">Poslednja izmena: {updated}</p>

      <div className="mt-8 space-y-6 text-[15px] leading-relaxed text-muted">{children}</div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-body">{heading}</h2>
      {children}
    </section>
  );
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="ml-5 list-disc space-y-1.5">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

const UPDATED = '22. avgust 2026.';

export function PrivacyPage() {
  useSeo({
    title: `Politika privatnosti | ${SITE_NAME}`,
    description:
      'Koje podatke KojaKola prikuplja, zašto ih prikuplja i šta možete da uradite sa njima. ' +
      'Bez reklamnih mreža i bez praćenja između sajtova.',
    canonicalPath: '/privatnost',
  });

  return (
    <Prose title="Politika privatnosti" updated={UPDATED}>
      <p>
        KojaKola je besplatan oglasnik za automobile. Ova politika objašnjava koje podatke
        prikupljamo, zašto ih prikupljamo i šta možete da uradite sa njima. Trudimo se da tražimo
        onoliko koliko je neophodno da bi oglasnik radio — ništa više.
      </p>

      <Section heading="Podaci koje prikupljamo">
        <p>Kada napravite nalog i koristite sajt, čuvamo:</p>
        <List
          items={[
            'Podatke naloga: e-mail adresa, ime koje prikazujete, lozinka u kriptovanom obliku (nikada u čitljivom).',
            'Profil, ako ga popunite: telefon, grad, fotografija profila, naziv firme i kratak opis.',
            'Oglase koje postavite: sve podatke o vozilu, fotografije, opis i kontakt telefon.',
            'Poruke koje razmenjujete sa drugim korisnicima, kao i ocene i komentare koje ostavite.',
            'Sačuvane oglase i sačuvane pretrage.',
            'Tehničke podatke: IP adresa, opis uređaja i pregledača uz svaku prijavu, kako biste mogli da prepoznate svoje sesije.',
          ]}
        />
        <p>
          Ako se prijavite preko Google ili Facebook naloga, od njih dobijamo samo e-mail adresu, ime
          i fotografiju profila. Vašu lozinku kod tih servisa ne vidimo i ne čuvamo.
        </p>
      </Section>

      <Section heading="Zašto ih prikupljamo">
        <List
          items={[
            'Da biste mogli da se prijavite i da vaši oglasi ostanu vaši.',
            'Da bi kupci mogli da vas kontaktiraju, a vi njih.',
            'Da bismo mogli da reagujemo na prijave sumnjivih oglasa i sprečimo zloupotrebe.',
            'Da bismo vas obavestili o novoj poruci ili novoj oceni — u aplikaciji, i push obaveštenjem ako ga dozvolite.',
          ]}
        />
        <p>
          Vaše podatke ne prodajemo, ne iznajmljujemo i ne ustupamo oglašivačima. Na sajtu nema
          reklama, pa nema ni razloga da to ikada radimo.
        </p>
      </Section>

      <Section heading="Šta je javno">
        <p>
          Oglas koji objavite je javan — vidljiv je svima i indeksiraju ga pretraživači. Uz oglas se
          prikazuju ime koje ste izabrali, grad, fotografije vozila i, ako tako podesite, vaš telefon.
          Vaša e-mail adresa nikada nije javna. Poruke su vidljive samo vama i osobi sa kojom se
          dopisujete.
        </p>
      </Section>

      <Section heading="Kome poveravamo podatke">
        <p>
          Koristimo servise koji nam pomažu da sajt radi. Oni obrađuju podatke isključivo po našem
          nalogu:
        </p>
        <List
          items={[
            'Microsoft Azure — hosting, baza podataka, skladište fotografija i praćenje grešaka.',
            'Google i Facebook — samo ako izaberete prijavu preko njihovog naloga.',
            'Servis za slanje e-mail poruka sa sistemskim obaveštenjima.',
            'Servis za istoriju vozila, kada proveravate VIN broj — njemu se šalje samo VIN.',
          ]}
        />
      </Section>

      <Section heading="Kolačići">
        <p>
          Koristimo jedan neophodan kolačić, koji drži vašu prijavu aktivnom. Nema kolačića za
          praćenje, profilisanje niti oglašavanje. Podešavanje teme i sitne izbore u interfejsu
          čuvamo lokalno u vašem pregledaču i oni nikada ne stižu do nas.
        </p>
      </Section>

      <Section heading="Koliko dugo čuvamo podatke">
        <p>
          Podatke naloga čuvamo dok nalog postoji. Oglasi ističu nakon 60 dana, ali ostaju vezani za
          vaš nalog dok ih ne obrišete. Kada obrišete nalog, brišemo i sve navedeno — trajno i bez
          mogućnosti povraćaja.
        </p>
      </Section>

      <Section heading="Vaša prava">
        <p>
          Imate pravo da vidite koje podatke o vama čuvamo, da ih ispravite i da ih obrišete. Profil
          i oglase možete da izmenite u svakom trenutku. Nalog možete da obrišete sami — uputstvo je
          na stranici{' '}
          <Link to="/brisanje-naloga" className="text-accent hover:underline">
            brisanje naloga
          </Link>
          . Za sve ostalo pišite nam na{' '}
          <a href="mailto:podrska@kojakola.com" className="text-accent hover:underline">
            podrska@kojakola.com
          </a>
          .
        </p>
      </Section>

      <Section heading="Deca">
        <p>
          Sajt nije namenjen osobama mlađim od 16 godina i svesno ne prikupljamo njihove podatke.
        </p>
      </Section>

      <Section heading="Izmene">
        <p>
          Ako ovu politiku bitno izmenimo, obavestićemo vas u aplikaciji pre nego što izmena počne da
          važi.
        </p>
      </Section>
    </Prose>
  );
}

export function TermsPage() {
  useSeo({
    title: `Uslovi korišćenja | ${SITE_NAME}`,
    description:
      'Pravila korišćenja oglasnika KojaKola — šta smete da postavite, šta ne, i šta se dešava ' +
      'sa oglasom koji ih prekrši. Kratko i razumljivo.',
    canonicalPath: '/uslovi',
  });

  return (
    <Prose title="Uslovi korišćenja" updated={UPDATED}>
      <p>
        Korišćenjem sajta KojaKola prihvatate ove uslove. Napisani su da budu kratki i razumljivi,
        jer uslovi koje niko ne pročita nikome ne koriste.
      </p>

      <Section heading="Šta je KojaKola">
        <p>
          KojaKola je besplatan oglasnik za automobile. Postavljanje oglasa, pretraga i dopisivanje
          su besplatni i nemamo nameru da to menjamo. Nismo prodavac, kupac, posrednik ni jemac ni u
          jednoj transakciji — mi samo spajamo dve strane.
        </p>
      </Section>

      <Section heading="Vaš nalog">
        <p>
          Za postavljanje oglasa i dopisivanje potreban je nalog. Odgovorni ste za tačnost podataka i
          za čuvanje lozinke. Jedna osoba ne treba da otvara više naloga radi zaobilaženja
          ograničenja ili blokade.
        </p>
      </Section>

      <Section heading="Pravila za oglase">
        <p>Oglas mora da opisuje stvarno vozilo koje zaista prodajete. Nije dozvoljeno:</p>
        <List
          items={[
            'Objavljivati netačne podatke o vozilu, stanju, kilometraži ili ceni.',
            'Koristiti tuđe fotografije ili fotografije koje ne prikazuju vozilo iz oglasa.',
            'Postavljati isti automobil u više oglasa istovremeno.',
            'Objavljivati uvredljiv, nezakonit ili neprimeren sadržaj.',
            'Koristiti sajt za prevaru, spam ili preprodaju kontakata.',
            'Nuditi vozilo koje nije u vašem vlasništvu ili koje ne smete da prodate.',
          ]}
        />
      </Section>

      <Section heading="Sadržaj koji objavljujete">
        <p>
          Sadržaj koji postavite ostaje vaš. Objavljivanjem nam dajete pravo da ga prikazujemo na
          sajtu i u pretraživačima, isključivo u svrhu prikazivanja oglasa. To pravo prestaje kada
          obrišete oglas ili nalog.
        </p>
      </Section>

      <Section heading="Prijave i moderacija">
        <p>
          Svaki oglas može da se prijavi dugmetom „Prijavi oglas“. Prijave pregledamo i zadržavamo
          pravo da uklonimo oglas ili ograničimo nalog koji krši ova pravila, bez prethodne najave
          kada je šteta očigledna. Svakog korisnika možete i da blokirate, čime prekidate prepisku sa
          njim.
        </p>
      </Section>

      <Section heading="Odgovornost">
        <p>
          Ne garantujemo tačnost oglasa niti ishod bilo koje prodaje. Proverite vozilo, dokumentaciju
          i sagovornika pre nego što platite bilo šta. Sajt se pruža „takav kakav jeste“ i ne
          odgovaramo za štetu nastalu iz dogovora između korisnika.
        </p>
      </Section>

      <Section heading="Prestanak korišćenja">
        <p>
          Nalog možete da obrišete u svakom trenutku — vidite{' '}
          <Link to="/brisanje-naloga" className="text-accent hover:underline">
            brisanje naloga
          </Link>
          . Mi možemo da ukinemo nalog koji ozbiljno ili u kontinuitetu krši ova pravila.
        </p>
      </Section>

      <Section heading="Kontakt">
        <p>
          Za sve nedoumice pišite na{' '}
          <a href="mailto:podrska@kojakola.com" className="text-accent hover:underline">
            podrska@kojakola.com
          </a>
          .
        </p>
      </Section>
    </Prose>
  );
}

/**
 * The public explanation of how to delete an account.
 *
 * Deliberately reachable with no account and no app. Google Play requires a URL that anyone can
 * open — including somebody who has already uninstalled — and this is it.
 */
export function DeleteAccountInfoPage() {
  useSeo({
    title: `Brisanje naloga | ${SITE_NAME}`,
    description:
      'Kako da obrišete nalog na sajtu KojaKola i šta se tačno briše sa njim — oglasi, ' +
      'fotografije, poruke i ocene. Bez kontaktiranja podrške.',
    canonicalPath: '/brisanje-naloga',
  });

  return (
    <Prose title="Brisanje naloga" updated={UPDATED}>
      <p>
        Nalog na sajtu KojaKola možete obrisati sami, u svakom trenutku, bez objašnjenja i bez
        kontaktiranja podrške.
      </p>

      <Section heading="Kako se briše">
        <ol className="ml-5 list-decimal space-y-1.5">
          <li>Prijavite se na svoj nalog.</li>
          <li>
            Otvorite{' '}
            <Link to="/podesavanja" className="text-accent hover:underline">
              Podešavanja
            </Link>{' '}
            iz menija u zaglavlju.
          </li>
          <li>Na dnu stranice izaberite „Obriši nalog“.</li>
          <li>Potvrdite upisivanjem reči koja se traži. Brisanje je trenutno i konačno.</li>
        </ol>
      </Section>

      <Section heading="Šta se briše">
        <p>Brisanjem naloga trajno nestaju:</p>
        <List
          items={[
            'Vaš nalog, e-mail adresa, telefon i svi podaci profila.',
            'Svi vaši oglasi, zajedno sa fotografijama.',
            'Sve prepiske u kojima ste učestvovali, sa obe strane.',
            'Ocene i komentari koje ste napisali, kao i ocene koje ste dobili.',
            'Sačuvani oglasi, sačuvane pretrage i sva obaveštenja.',
          ]}
        />
        <p>
          Brisanje se ne može poništiti i podaci se ne mogu vratiti. Anonimne prijave koje su drugi
          korisnici podneli protiv nekog oglasa ostaju u sistemu moderacije, ali bez ikakve veze sa
          vašim nalogom.
        </p>
      </Section>

      <Section heading="Nemate pristup nalogu?">
        <p>
          Ako ne možete da se prijavite, pišite nam sa e-mail adrese sa kojom je nalog otvoren na{' '}
          <a href="mailto:podrska@kojakola.com" className="text-accent hover:underline">
            podrska@kojakola.com
          </a>{' '}
          i obrisaćemo nalog umesto vas, najkasnije u roku od 30 dana.
        </p>
      </Section>
    </Prose>
  );
}
